import React, { useState, useEffect } from 'react';
import Board from './Board';
import '../styles/Game.css';
import { selectColumnForColor, selectAiShiftMove } from '../utils/gameAI';

const BOARD_SIZE_PRESETS = [
  { label: '5 x 6 (default)', rows: 5, cols: 6 },
  { label: '6 x 7', rows: 6, cols: 7 },
  { label: '7 x 8', rows: 7, cols: 8 },
];

const MIN_DIMENSION = 4;
const MAX_DIMENSION = 10;

const Game = () => {
  // Create an empty board of the given size
  const createEmptyBoard = (rows, cols) => {
    return Array(rows).fill().map(() => Array(cols).fill(null));
  };

  const [boardSize, setBoardSize] = useState({ rows: 5, cols: 6 });
  const [useCustomSize, setUseCustomSize] = useState(false);
  const [customRows, setCustomRows] = useState(5);
  const [customCols, setCustomCols] = useState(6);

  const [board, setBoard] = useState(createEmptyBoard(5, 6));
  const [currentPlayer, setCurrentPlayer] = useState('red');
  const [gameStatus, setGameStatus] = useState('setup'); // 'setup', 'playing', 'draw', 'win'
  const [winner, setWinner] = useState(null);
  const [lastMove, setLastMove] = useState(null);
  const [shiftedRow, setShiftedRow] = useState(null);
  const [gamePhase, setGamePhase] = useState('drop'); // 'interim', 'drop', or 'shift'
  const [fallingChips, setFallingChips] = useState([]);
  const [winningPositions, setWinningPositions] = useState([]);
  const [gameMode, setGameMode] = useState(null); // null, 'twoPlayer', 'singlePlayer'
  // Always use hard difficulty for AI
  const aiDifficulty = 'hard';
  const [playerColor, setPlayerColor] = useState('red'); // Player's chip color in single player mode

  // A chip that was pushed out of the board and is now in the current player's hand.
  // When set, the player's turn begins with an interim placement of this chip
  // (anywhere they want, no push required) before their normal turn.
  const [handChip, setHandChip] = useState(null);

  const ROWS = board.length;
  const COLS = board[0] ? board[0].length : 0;

  // Clear animation state after a drop, regardless of what gamePhase does in
  // the meantime (e.g. moving on to the push) - otherwise this timer gets
  // cancelled by the phase change without ever actually clearing lastMove,
  // which can cause the drop animation to incorrectly replay later.
  useEffect(() => {
    if (!lastMove) return;

    const timer = setTimeout(() => {
      setLastMove(null);
    }, 1000); // Slightly longer than animation duration

    return () => clearTimeout(timer);
  }, [lastMove]);

  // Clear row shift animation
  useEffect(() => {
    if (shiftedRow !== null) {
      const timer = setTimeout(() => {
        setShiftedRow(null);
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [shiftedRow]);

  // AI move logic - AI moves are handled directly in the effect to avoid dependency issues
  useEffect(() => {
    // Only make AI move if it's single player mode and it's AI's turn
    if (gameMode === 'singlePlayer' && gameStatus === 'playing' &&
        currentPlayer !== playerColor) {
      let aiMoveTimer;
      const aiPlayer = currentPlayer;
      const humanPlayer = playerColor;

      const handleAiColumnSelection = () => {
        if (gameStatus !== 'playing' || (gamePhase !== 'interim' && gamePhase !== 'drop')) return;

        const colorToPlace = gamePhase === 'interim' ? handChip : aiPlayer;

        const selectedColumn = selectColumnForColor(board, colorToPlace, aiPlayer, humanPlayer, checkForWin);

        placeChip(selectedColumn);
      };

      const handleAiShiftSelection = () => {
        if (gameStatus !== 'playing' || gamePhase !== 'shift') return;

        const { row, direction } = selectAiShiftMove(board, aiPlayer, aiDifficulty, checkForWin);

        shiftRow(row, direction);
      };

      if (gamePhase === 'interim' || gamePhase === 'drop') {
        // Add delay to make it seem like AI is thinking
        aiMoveTimer = setTimeout(() => {
          if (gameStatus === 'playing' && (gamePhase === 'interim' || gamePhase === 'drop') &&
              gameMode === 'singlePlayer' && currentPlayer !== playerColor) {
            handleAiColumnSelection();
          }
        }, 1000);
      } else if (gamePhase === 'shift') {
        // Add delay for AI shift decision
        aiMoveTimer = setTimeout(() => {
          if (gameStatus === 'playing' && gamePhase === 'shift' &&
              gameMode === 'singlePlayer' && currentPlayer !== playerColor) {
            handleAiShiftSelection();
          }
        }, 1200);
      }

      return () => {
        if (aiMoveTimer) clearTimeout(aiMoveTimer);
      };
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameMode, gameStatus, currentPlayer, playerColor, gamePhase, board, aiDifficulty, handChip]);

  // Apply gravity to make chips fall if there's no support below
  const applyGravity = (board) => {
    const newBoard = board.map(row => [...row]);
    const fallingPositions = [];

    // Check columns from bottom to top and left to right
    for (let col = 0; col < COLS; col++) {
      for (let row = ROWS - 2; row >= 0; row--) {
        if (newBoard[row][col] && newBoard[row + 1][col] === null) {
          // Found a floating chip, make it fall
          let finalRow = row;

          // Find how far it can fall
          for (let r = row + 1; r < ROWS; r++) {
            if (newBoard[r][col] === null) {
              finalRow = r;
            } else {
              break;
            }
          }

          if (finalRow !== row) {
            // Record the falling animation data
            fallingPositions.push({
              from: { row, col },
              to: { row: finalRow, col },
              color: newBoard[row][col]
            });

            // Move the chip
            newBoard[finalRow][col] = newBoard[row][col];
            newBoard[row][col] = null;
          }
        }
      }
    }

    // Replace the original board with gravity-applied board
    for (let r = 0; r < ROWS; r++) {
      for (let c = 0; c < COLS; c++) {
        board[r][c] = newBoard[r][c];
      }
    }

    return fallingPositions;
  };

  // Check for a win of a specific color and return winning positions
  const checkForWin = (board, row, col, player) => {
    if (row < 0 || row >= board.length || col < 0 || col >= board[0].length || !player) {
      return null;
    }

    const directions = [
      [0, 1],  // horizontal
      [1, 0],  // vertical
      [1, 1],  // diagonal down-right
      [1, -1], // diagonal down-left
    ];

    for (const [dx, dy] of directions) {
      let count = 1;
      const positions = [{row, col}];

      for (const factor of [1, -1]) {
        let r = row + factor * dx;
        let c = col + factor * dy;

        while (
          r >= 0 && r < board.length &&
          c >= 0 && c < board[0].length &&
          board[r][c] === player
        ) {
          positions.push({row: r, col: c});
          count++;
          r += factor * dx;
          c += factor * dy;
        }
      }

      if (count >= 4) {
        return positions;
      }
    }

    return null;
  };

  // Scan the entire board for a win of either color. A win always belongs to
  // whichever color actually has 4 in a row, regardless of whose turn it is or
  // who placed the deciding chip.
  const findAnyColorWin = (board) => {
    for (const color of ['red', 'blue']) {
      for (let r = 0; r < board.length; r++) {
        for (let c = 0; c < board[0].length; c++) {
          if (board[r][c] === color) {
            const positions = checkForWin(board, r, c, color);
            if (positions) return { positions, color };
          }
        }
      }
    }
    return null;
  };

  // Check if the board is full (draw)
  const isBoardFull = (board) => {
    return board[0].every(cell => cell !== null);
  };

  // End the current turn: hand off any ejected chip to the next player. If a
  // chip was ejected, the next player's turn starts with an interim placement
  // of it before their normal turn.
  const finishTurn = (ejectedColorForNext) => {
    const nextPlayer = currentPlayer === 'red' ? 'blue' : 'red';
    setCurrentPlayer(nextPlayer);
    if (ejectedColorForNext) {
      setHandChip(ejectedColorForNext);
      setGamePhase('interim');
    } else {
      setHandChip(null);
      setGamePhase('drop');
    }
  };

  // Function to shift a row left or right, ejecting the edge chip instead of
  // wrapping it to the other side. This is the only mandatory push of a turn -
  // it always happens after any interim placement and the normal drop.
  const shiftRow = (rowIndex, direction) => {
    if (gameStatus !== 'playing' || gamePhase !== 'shift') {
      return;
    }

    const newBoard = board.map(row => [...row]);
    const row = newBoard[rowIndex];
    let ejectedColor;

    if (direction === 'left') {
      // The leftmost chip is pushed out of the board entirely
      ejectedColor = row.shift();
      row.push(null);
    } else {
      // The rightmost chip is pushed out of the board entirely
      ejectedColor = row.pop();
      row.unshift(null);
    }

    // Apply gravity to make chips fall if there's no support below
    const fallingChipsData = applyGravity(newBoard);
    setFallingChips(fallingChipsData);

    // Record the shifted row for animation
    setShiftedRow({ index: rowIndex, direction });

    // Update the board state
    setBoard(newBoard);

    // The turn's mandatory push has now happened, so its outcome can be
    // finalized: a win or draw is only ever declared here.
    const win = findAnyColorWin(newBoard);

    if (win) {
      setGameStatus('win');
      setWinner(win.color);
      setWinningPositions(win.positions);
    } else if (isBoardFull(newBoard)) {
      setGameStatus('draw');
    } else {
      finishTurn(ejectedColor);
    }
  };

  // Handle a column click during the 'interim' or 'drop' phase. The 'interim'
  // placement (the received chip) is checked for a win/draw immediately,
  // since it has no push of its own. The normal 'drop' is not checked here -
  // that placement's outcome is only finalized after its mandatory push.
  const placeChip = (colIndex) => {
    if (gameStatus !== 'playing' || (gamePhase !== 'interim' && gamePhase !== 'drop')) {
      return;
    }

    // Find the lowest empty row in the selected column
    let rowIndex = ROWS - 1;
    while (rowIndex >= 0 && board[rowIndex][colIndex]) {
      rowIndex--;
    }

    // If the column is full, do nothing
    if (rowIndex < 0) {
      return;
    }

    const colorToPlace = gamePhase === 'interim' ? handChip : currentPlayer;

    // Create a new board with the move
    const newBoard = board.map(row => [...row]);
    newBoard[rowIndex][colIndex] = colorToPlace;

    // Update board and set last move for animation
    setBoard(newBoard);
    setLastMove({ row: rowIndex, col: colIndex });

    if (gamePhase === 'interim') {
      // The received chip has been placed anywhere the player chose. No push
      // is attached to this placement, so its outcome is finalized right away.
      const placedWinPositions = checkForWin(newBoard, rowIndex, colIndex, colorToPlace);
      const win = placedWinPositions ? { positions: placedWinPositions, color: colorToPlace } : findAnyColorWin(newBoard);

      if (win) {
        setGameStatus('win');
        setWinner(win.color);
        setWinningPositions(win.positions);
        return;
      }

      if (isBoardFull(newBoard)) {
        setGameStatus('draw');
        return;
      }

      // No win or draw - the player now moves on to their normal turn.
      setHandChip(null);
      setGamePhase('drop');
      return;
    }

    // Normal drop done - the mandatory push comes next.
    setGamePhase('shift');
  };

  // Reset the game
  const resetGame = () => {
    setBoard(createEmptyBoard(boardSize.rows, boardSize.cols));
    setCurrentPlayer('red');
    setGameStatus('setup');
    setWinner(null);
    setLastMove(null);
    setShiftedRow(null);
    setGamePhase('drop');
    setFallingChips([]);
    setWinningPositions([]);
    setGameMode(null);
    setHandChip(null);
  };

  // Start a new game with chosen settings
  const startGame = (mode, color = 'red') => {
    const size = useCustomSize
      ? { rows: customRows, cols: customCols }
      : boardSize;

    setBoardSize(size);
    setGameMode(mode);
    setPlayerColor(color);
    setBoard(createEmptyBoard(size.rows, size.cols));
    setCurrentPlayer('red'); // Red always goes first
    setGameStatus('playing');
    setWinner(null);
    setLastMove(null);
    setShiftedRow(null);
    setGamePhase('drop');
    setFallingChips([]);
    setWinningPositions([]);
    setHandChip(null);
  };

  const currentDropColor = gamePhase === 'interim' ? handChip : currentPlayer;

  const isHumanControllingActivePhase = gameMode !== 'singlePlayer' || currentPlayer === playerColor;

  return (
    <div className="game">
      <h1>Revolving Chain Four</h1>

      {gameStatus === 'setup' ? (
        // Game setup screen
        <div className="game-setup">
          <h2>Game Mode</h2>
          <div className="setup-options">
            <button
              className={`mode-button ${gameMode === 'twoPlayer' ? 'selected' : ''}`}
              onClick={() => setGameMode('twoPlayer')}
            >
              Two Player
            </button>
            <button
              className={`mode-button ${gameMode === 'singlePlayer' ? 'selected' : ''}`}
              onClick={() => setGameMode('singlePlayer')}
            >
              Single Player
            </button>
          </div>

          {gameMode === 'singlePlayer' && (
            <>
              <h2>Choose Your Color</h2>
              <div className="setup-options">
                <button
                  className={`color-button ${playerColor === 'red' ? 'selected' : ''}`}
                  onClick={() => setPlayerColor('red')}
                >
                  <div className="color-preview red"></div>
                  Red
                </button>
                <button
                  className={`color-button ${playerColor === 'blue' ? 'selected' : ''}`}
                  onClick={() => setPlayerColor('blue')}
                >
                  <div className="color-preview blue"></div>
                  Blue
                </button>
              </div>
            </>
          )}

          <h2>Board Size</h2>
          <div className="setup-options">
            {BOARD_SIZE_PRESETS.map((preset) => (
              <button
                key={preset.label}
                className={`mode-button ${!useCustomSize && boardSize.rows === preset.rows && boardSize.cols === preset.cols ? 'selected' : ''}`}
                onClick={() => {
                  setUseCustomSize(false);
                  setBoardSize({ rows: preset.rows, cols: preset.cols });
                }}
              >
                {preset.label}
              </button>
            ))}
            <button
              className={`mode-button ${useCustomSize ? 'selected' : ''}`}
              onClick={() => setUseCustomSize(true)}
            >
              Custom
            </button>
          </div>

          {useCustomSize && (
            <div className="custom-size-inputs">
              <label>
                Rows
                <input
                  type="number"
                  min={MIN_DIMENSION}
                  max={MAX_DIMENSION}
                  value={customRows}
                  onChange={(e) => setCustomRows(Math.min(MAX_DIMENSION, Math.max(MIN_DIMENSION, Number(e.target.value) || MIN_DIMENSION)))}
                />
              </label>
              <label>
                Columns
                <input
                  type="number"
                  min={MIN_DIMENSION}
                  max={MAX_DIMENSION}
                  value={customCols}
                  onChange={(e) => setCustomCols(Math.min(MAX_DIMENSION, Math.max(MIN_DIMENSION, Number(e.target.value) || MIN_DIMENSION)))}
                />
              </label>
            </div>
          )}

          <button
            className="start-button"
            disabled={!gameMode}
            onClick={() => startGame(gameMode, playerColor)}
          >
            Start Game
          </button>
        </div>
      ) : (
        // Active game screen
        <>
          <div className="game-info">
            {gameStatus === 'playing' ? (
              <div className="current-player">
                <span>Current Player: </span>
                <div className={`player-indicator ${currentPlayer}`}></div>
                {gameMode === 'singlePlayer' && currentPlayer !== playerColor && (
                  <span className="ai-thinking">AI is thinking...</span>
                )}
              </div>
            ) : gameStatus === 'win' ? (
              <div className="winner">
                <div className={`player-indicator ${winner}`}></div>
                <span> WINS!</span>
                {gameMode === 'singlePlayer' && (
                  <span> {winner === playerColor ? 'You won!' : 'AI won!'}</span>
                )}
              </div>
            ) : (
              <div className="draw">It's a draw!</div>
            )}
          </div>

          {gameStatus === 'playing' && gamePhase === 'interim' && (
            <div className="hand-chip-banner">
              <div className={`player-indicator ${handChip}`}></div>
              <span>
                {isHumanControllingActivePhase
                  ? `You received a pushed-out ${handChip} chip! Place it anywhere, then take your normal turn.`
                  : `AI received a pushed-out chip and is placing it.`}
              </span>
            </div>
          )}

          <Board
            board={board}
            handleColumnClick={placeChip}
            handleRowShift={shiftRow}
            lastMove={lastMove}
            shiftedRow={shiftedRow}
            fallingChips={fallingChips}
            gamePhase={gamePhase}
            winningPositions={winningPositions}
            activeColor={currentDropColor}
          />

          <div className="game-controls">
            <button className="reset-button" onClick={resetGame}>
              New Game
            </button>
            {gameMode === 'singlePlayer' && (
              <div className="game-info-label">
                You are playing as <div className={`player-indicator ${playerColor}`}></div>
                against AI
              </div>
            )}
          </div>

          <div className="game-rules">
            <h3>Game Rules:</h3>
            <p>
              Connect four of your chips in a row (horizontally, vertically, or diagonally) to win.
              After placing a chip, you must push a row left or right. Any unsupported chips will fall down!
              Pushing a row no longer wraps it around: the chip at the edge you push toward is pushed
              completely out of the board. It goes to the next player, who places it anywhere on the
              board first (no push required for that), then takes their normal turn: drop their own
              chip and make the one mandatory push.
            </p>
            {gamePhase === 'shift' && gameStatus === 'playing' && (
              <div className="shift-instruction">
                <p className="highlight">Choose a row to push left or right!</p>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default Game;
